import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormGroup } from "@angular/forms";
import { ToastController } from "@ionic/angular";
import { BaseClass } from "../../../../classes/base";
import { QuestionConfig, QuestionFormulaType, ValuesGlobalization } from "../../../../interfaces/process-config.interface";
import { TranslationService } from "../../../../services/translation.service";

@Component({
    selector: "app-calculate-questions",
    templateUrl: "./calculate-questions.component.html",
    styleUrls: ["./calculate-questions.component.scss", "../../question-types.component.scss"],
})
export class CalculateQuestionsComponent extends BaseClass implements OnInit {
    @Input() question: QuestionConfig;
    @Input() formGroup: UntypedFormGroup;
    maxValue: number;
    value: number;
    valueLabel: string;
    color: string = "var(--ion-color-primary)";
    showCalculateLabel: boolean;
    showQuestion: boolean;
    showQuestionName: boolean;
    currentLanguage: string;
    formControl: AbstractControl;
    hasValidValue: boolean;

    private possibleValues: { [value: string]: number };
    private possibleColors: { [value: string]: string };

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
    ) {
        super(_toastController, _translationService);
        this.currentLanguage = _translationService.getSelectedLanguage();
        _translationService.languageChanged$.subscribe((lang) => {
            this.currentLanguage = lang;
            if (this.showCalculateLabel) this._getQuestionConfigs();
        });
    }

    ngOnInit() {
        this.showQuestion = this.question.typeConfig.showQuestion;
        if (!this.showQuestion) return;

        const hasAllQuestionsSameType = this._hasAllQuestionsSameType(this.question.relatedQuestions.map((rq) => rq.question));

        this.showCalculateLabel = hasAllQuestionsSameType && this.question.typeConfig.showCalculateLabel;
        this.showQuestionName = this.question.typeConfig.showQuestionName;
        this.formControl = this.formGroup.controls["field" + this.question.order];

        this.value = this.formControl.value;
        this.hasValidValue = this._hasValidValue(this.value);

        if (this.showCalculateLabel) {
            this.formControl.valueChanges.subscribe((value) => {
                this.value = value;
                this.hasValidValue = this._hasValidValue(value);
                if (this.hasValidValue) {
                    this._calculateValueLabel(value);
                } else {
                    this.valueLabel = null;
                    this.color = "var(--ion-color-primary)";
                }
            });
            this._getQuestionConfigs();
        }
    }
    private _hasAllQuestionsSameType(questions: QuestionConfig[]): boolean {
        const questionTypes = questions.map((q) => q.typeConfig.id);
        return questionTypes.every((t) => t === questionTypes[0]);
    }
    private _getQuestionConfigs(): void {
        const typeConfig = this.question.relatedQuestions[0].question.typeConfig;
        this.maxValue = this.question.formula === QuestionFormulaType.Average ?  Math.max(...typeConfig.possibleValues) : Math.max(...typeConfig.possibleValues) * this.question?.relatedQuestions?.length;
        this._getPossibleValues(typeConfig.possibleValues, typeConfig.possibleValuesGlobalized, typeConfig.colors);
        if (this.hasValidValue) this._calculateValueLabel(this.formControl.value);
    }
    private _getPossibleValues(possibleValues: Array<any>, possibleValuesGlobalized: ValuesGlobalization, colors: Array<string>): void {
        this.possibleValues = {};
        this.possibleColors = {};
        possibleValuesGlobalized[this.currentLanguage].forEach((value, index) => {
            this.possibleValues[value] = possibleValues[index];
            this.possibleColors[value] = colors[index];
        });
    }

    private _hasValidValue(value: number): boolean {
        return value != 0 && value != null && !isNaN(value);
    }

    private _calculateValueLabel(value: number): void {
        if(this.question.typeConfig.possibleValues?.length > 0) {
            const values = this.question.typeConfig.possibleValues;
            let idx = 0;
            while(idx < values.length && value > values[idx]) {
                idx++;
            }
            this.valueLabel = this.question.typeConfig.possibleValuesGlobalized[this.currentLanguage][idx] || null;
            this.color = this.question.typeConfig.colors[idx];
        } else {
            const values = Object.values(this.possibleValues);
            const closest = values.filter((v) => v <= value).reduce((prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev));
            this.valueLabel = Object.keys(this.possibleValues).find((key) => this.possibleValues[key] === closest);
            this.color = this.possibleColors[this.valueLabel];
        }
    }

    protected readonly isNaN = isNaN;
}
